import classNames from "classnames";

import { UsageLimit } from "@/shared/components/UsageLimit";
import {
  type InvocationQueueStats,
  type WaitEstimate,
  estimateWait,
  formatWaitEstimate,
} from "@/shared/domain/actions";

import styles from "./InvocationActivity.module.css";
import type { PanelFigures } from "./useActivityPanelData";

/** Shown in place of every figure when any of the panel's queries fails. */
export const ACTIVITY_LOAD_ERROR =
  "Could not load invocation activity. Nothing shown here is current.";

/** The figures a reader checks first, one card each. */
export function ActivityFigureCards({
  figures,
  queueStats,
  filtered,
}: {
  figures: PanelFigures;
  queueStats: InvocationQueueStats | undefined;
  /**
   * Whether the page narrows what the figures count. The concurrency limit and the wait estimate
   * are the org's either way, so the cards say whose numbers they are once the two differ.
   */
  filtered: boolean;
}) {
  const {
    concurrencyLimit,
    runningNow,
    waitingNow,
    finished,
    failed,
    credits,
  } = figures;
  const cap =
    concurrencyLimit !== null && concurrencyLimit > 0 ? concurrencyLimit : null;
  const wait = queueStats === undefined ? undefined : estimateWait(queueStats);
  return (
    <div className={styles.cardsContainer}>
      <div className={styles.cards}>
        <UsageLimit
          compact
          title="Running now"
          currentValue={runningNow.toLocaleString()}
          currentPercent={cap === null ? undefined : (runningNow / cap) * 100}
          // Always says whose cap it is: the same reader sees this card on the org's list and on
          // a dataset's, and a label that changes between the two reads as two different limits.
          limitValue={
            cap === null
              ? undefined
              : `${cap.toLocaleString()} concurrent, org-wide`
          }
          monthly={false}
          detail={concurrencyLimit === null ? "No concurrency cap" : undefined}
          info={
            filtered
              ? "This view's share of the org's concurrency limit."
              : undefined
          }
        />
        <UsageLimit
          compact
          title="Waiting now"
          currentValue={waitingNow.toLocaleString()}
          detail={waitDetail(wait)}
          info={waitExplanation(queueStats, wait, waitingNow, filtered)}
        />
        <UsageLimit
          compact
          title="Finished in window"
          currentValue={finished.toLocaleString()}
          detail={`${failed.toLocaleString()} failed`}
        />
        <UsageLimit
          compact
          title="Processing units in window"
          currentValue={formatCredits(credits)}
          detail="RPU, spread over each run"
        />
      </div>
    </div>
  );
}

/** The same four figures in one line, for the collapsed heading. */
export function ActivityFigureSummary({
  figures,
  loading,
  failed,
  previous = false,
}: {
  figures: PanelFigures;
  loading: boolean;
  failed: boolean;
  /** The figures answer the previous request; the new one is still loading. */
  previous?: boolean;
}) {
  if (failed) {
    return (
      <p className={styles.summary} role="alert" data-testid="activity-summary">
        {ACTIVITY_LOAD_ERROR}
      </p>
    );
  }
  return (
    <p
      className={classNames(styles.summary, {
        [styles.contentPrevious]: previous,
      })}
      data-testid="activity-summary"
    >
      {loading ? "Loading…" : summaryText(figures)}
    </p>
  );
}

function summaryText(figures: PanelFigures): string {
  const running =
    figures.concurrencyLimit !== null && figures.concurrencyLimit > 0
      ? `${figures.runningNow.toLocaleString()} of ${figures.concurrencyLimit.toLocaleString()} running`
      : `${figures.runningNow.toLocaleString()} running`;
  const finished =
    figures.failed > 0
      ? `${figures.finished.toLocaleString()} finished (${figures.failed.toLocaleString()} failed)`
      : `${figures.finished.toLocaleString()} finished`;
  return [
    running,
    `${figures.waitingNow.toLocaleString()} waiting`,
    finished,
    `${formatCredits(figures.credits)} RPU`,
  ].join(" · ");
}

function formatCredits(credits: number): string {
  return credits.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/**
 * The line under the waiting count. It names new invocations outright: said of "work", the time
 * read as how long until anything at all starts, rather than how long something submitted now waits.
 */
function waitDetail(wait: WaitEstimate | undefined): string {
  if (wait === undefined) {
    return "";
  }
  switch (wait.kind) {
    case "estimated":
      return `New invocations start in ${formatWaitEstimate(wait.seconds)}`;
    case "starts-now":
      return "A new invocation starts right away";
    case "unknown":
      return "No estimate yet";
  }
}

/** What the estimate means, how it is calculated, and whose queue it is about. */
function waitExplanation(
  stats: InvocationQueueStats | undefined,
  wait: WaitEstimate | undefined,
  waitingNow: number,
  filtered: boolean,
): string {
  const limit = stats?.concurrency_limit ?? null;
  const median = stats?.median_duration_seconds ?? null;
  if (limit === null || limit <= 0 || median === null) {
    return "The estimate needs a concurrency limit and at least one invocation finished in the last 7 days.";
  }
  if (wait?.kind === "starts-now") {
    return "Nothing is waiting and a slot is free, so a new invocation starts as soon as it's submitted.";
  }
  const model = waitModel(stats, limit, median);
  if (waitingNow > 0) {
    const scope = filtered
      ? " The count is for this view. The time covers your org's whole queue, so these may start sooner."
      : "";
    return `Roughly when an invocation submitted now would start. The last waiting invocation starts at about the same time. ${model}${scope}`;
  }
  const scope = filtered
    ? " The count is for this view; the wait is for your org's whole queue."
    : "";
  return `Roughly how long an invocation submitted now would wait for a free slot. ${model}${scope}`;
}

/**
 * Each branch states what the matching branch of {@link estimateWait} computes, and the two
 * estimating models differ: with `queued_work_seconds` the queue is divided by the concurrency
 * limit, without it the estimate counts whole rounds. That module lives elsewhere and nothing
 * checks the wording still matches the arithmetic.
 */
function waitModel(
  stats: InvocationQueueStats | undefined,
  limit: number,
  median: number,
): string {
  if (
    stats?.queued_work_seconds !== null &&
    stats?.queued_work_seconds !== undefined
  ) {
    return (
      `Each waiting invocation counts as its action's median runtime over the last 7 days, or ` +
      `your org's median if that action hasn't finished one recently. The total is split across ` +
      `your ${limit.toLocaleString()} concurrent slots, plus one median runtime ` +
      `(${formatRuntime(median)}) for work already running. Actual runtimes vary.`
    );
  }
  return (
    `Every waiting invocation is assumed to take your org's median runtime over the last 7 days ` +
    `(${formatRuntime(median)}), with ${limit.toLocaleString()} running at a time, plus one more ` +
    `for work already running. Actual runtimes vary.`
  );
}

function formatRuntime(seconds: number): string {
  if (seconds < 90) {
    return `${Math.max(1, Math.round(seconds))} s`;
  }
  const minutes = seconds / 60;
  if (minutes < 90) {
    return `${Math.round(minutes)} min`;
  }
  return `${(minutes / 60).toLocaleString(undefined, { maximumFractionDigits: 1 })} hr`;
}
